import { FC, useEffect } from "react";
import { Divider, Grid } from "@mui/material";
import { useFormikContext } from "formik";
import {
  CheckboxInput,
  DateInput,
  RadioInput,
  ReferenceDropDown,
  TextInput,
} from "../../../components";
import { onlyNumberKey, locale, SignitureTypeOptions } from "../../../utils";
import {
  ContactInfoArray,
  ForeignInput,
  IdentityCardTypeInput,
  IsAuthorizedSignature,
  KvkkInputs,
  NewIdentityInput,
  PassportInfoPanel,
  PassportInput,
  TCIdentityInput,
  BluecardInput,
} from "./common";
import useLocales from "../../../hooks/useLocales";
import { IndividualHandler, SetIndividualHandler } from "../common";
import TCForeignInput from "./common/TCForeignInput";

const RelatedPersonCreate: FC = () => {
  const { translate } = useLocales();
  const formik: any = useFormikContext();
  useEffect(() => {
    if (formik?.values?.contact?.contactCommunication[0]) {
      formik.setFieldValue(
        "email",
        formik?.values?.contact?.contactCommunication[0].value
      );
      formik.setFieldValue(
        "customerDetail.technicalEmail",
        formik?.values?.contact?.contactCommunication[0].value
      );
    }
  }, []);
  return (
    <Grid container spacing={3}>
      <IndividualHandler />
      <SetIndividualHandler />
      <Grid item md={12} lg={12} xs={12} sm={12}>
        <CheckboxInput
          name="contact.isAuthorizedPerson"
          label={translate(
            "resources.customers.related_persons.isAuthorizedPerson"
          )}
          disabled={true}
        />
        <IsAuthorizedSignature
          name="contact.isAuthorizedSignature"
          customerTypeId={formik.values.customerTypeId}
          customerKindId={formik.values.customerKindId}
        />
        <Divider />
      </Grid>
      <Grid item md={6} lg={6} xs={12} sm={12}>
        <TextInput
          name="contact.name"
          label={translate("resources.customers.related_persons.name")}
          disabled={formik.values.isExistCustomer}
        />
      </Grid>
      <Grid item md={6} lg={6} xs={12} sm={12}>
        <TextInput
          name="contact.surname"
          label={translate("resources.customers.related_persons.surname")}
          disabled={formik.values.isExistCustomer}
        />
      </Grid>

      <Grid item md={6} lg={6} xs={12} sm={12}>
        <ReferenceDropDown
          name="contact.jobTitleId"
          label={translate("resources.customers.related_persons.jobTitle")}
          reference="fixity/customer/jobTitle"
          optionName="name"
          disabled={formik.values.isExistCustomer ? true : false}
        />
      </Grid>
      <Grid item md={6} lg={6} xs={12} sm={12}>
        <ReferenceDropDown
          name="contact.departmentId"
          label={translate("resources.customers.related_persons.department")}
          reference="fixity/customer/department"
          optionName="name"
          disabled={formik.values.isExistCustomer}
        />
      </Grid>
      <Grid item md={12} lg={12} xs={12} sm={12}>
        {/* <LegalPermissionStatus
          legalPermission={formik.values.legalPermission}
        /> */}
        <Divider />
        <KvkkInputs
          kvkkMarkApprovalMailSend="contact.kvkkMarkApprovalMailSend"
          kvkkMarkApprovalSmsSend="contact.kvkkMarkApprovalSmsSend"
          customerTypeId={formik.values.customerTypeId}
        />
        <Divider />
      </Grid>
      {formik.values.contact.isAuthorizedPerson && (
        <>
          <Grid item md={12} lg={12} xs={12} sm={12}>
            <PassportInfoPanel
              title={translate(
                "resources.customers.related_persons.identityOrPasspartInfo"
              )}
            >
              <Grid item md={12} lg={12} xs={12} sm={12}>
                <Grid container spacing={3}>
                  <Grid item md={6} lg={6} xs={12} sm={12}>
                    <ReferenceDropDown
                      name="contact.nationalityId"
                      label={translate(
                        "resources.customers.related_persons.identityNationality"
                      )}
                      reference="fixity/customer/nationality"
                      optionName="name"
                      disabled={formik.values.isExistCustomer}
                    />
                  </Grid>
                  <Grid item md={6} lg={6} xs={12} sm={12}>
                    <IdentityCardTypeInput
                      name="contact.contactIdentityInformation.identityCardTypeId"
                      nationalityId={formik.values.contact.nationalityId}
                    />
                  </Grid>
                  <Grid item md={6} lg={6} xs={12} sm={12}>
                    <RadioInput
                      name="contact.signitureTypeId"
                      label={translate(
                        "resources.customers.related_persons.signitureType"
                      )}
                      radioGroup={locale(
                        SignitureTypeOptions,
                        "resources.customers.related_persons."
                      )}
                      disabled={formik.values.isExistCustomer}
                    />
                  </Grid>
                </Grid>
              </Grid>
            </PassportInfoPanel>
          </Grid>
          <Grid item md={6} lg={6} xs={12} sm={12}>
            <TextInput
              name="contact.identityNo"
              label={translate(
                "resources.customers.related_persons.tax_number"
              )}
              onKeyPressCapture={(event: any) => onlyNumberKey(event)}
              inputProps={{ maxLength: 11 }}
              disabled={formik.values.isExistCustomer}
            />
          </Grid>
          <Grid item md={6} lg={6} xs={12} sm={12}>
            <DateInput
              name="contact.dateOfBirth"
              label={translate(
                "resources.customers.related_persons.dateOfBirth"
              )}
              maxDate={new Date()}
              disabled={formik.values.isExistCustomer}
            />
          </Grid>
          <NewIdentityInput
            identityCardTypeId={
              formik.values.contact.contactIdentityInformation
                .identityCardTypeId
            }
            fatherName="contact.contactIdentityInformation.fatherName"
            motherName="contact.contactIdentityInformation.motherName"
            socialStatusId="contact.contactIdentityInformation.socialStatusId"
            documentNo="contact.contactIdentityInformation.documentNo"
            validDate="contact.contactIdentityInformation.validDate"
            genderId="contact.contactIdentityInformation.genderId"
          />
          <TCIdentityInput
            identityCardTypeId={
              formik.values.contact.contactIdentityInformation
                .identityCardTypeId
            }
            fatherName="contact.contactIdentityInformation.fatherName"
            motherName="contact.contactIdentityInformation.motherName"
            placeOfBirth="contact.contactIdentityInformation.placeOfBirth"
            socialStatusId="contact.contactIdentityInformation.socialStatusId"
            serialNo="contact.contactIdentityInformation.serialNo"
            documentNo="contact.contactIdentityInformation.documentNo"
            familyOrdernumber="contact.contactIdentityInformation.familyOrdernumber"
            orderNo="contact.contactIdentityInformation.orderNo"
            motherMaidenName="contact.contactIdentityInformation.motherMaidenName"
            volumeNo="contact.contactIdentityInformation.volumeNo"
            genderId="contact.contactIdentityInformation.genderId"
            identityProvince="contact.contactIdentityInformation.identityProvince"
            identityTown="contact.contactIdentityInformation.identityTown"
            issuedBy="contact.contactIdentityInformation.issuedBy"
            dateOfIssue="contact.contactIdentityInformation.dateOfIssue"
          />
          <PassportInput
            identityCardTypeId={
              formik.values.contact.contactIdentityInformation
                .identityCardTypeId
            }
            motherName="contact.contactIdentityInformation.motherName"
            fatherName="contact.contactIdentityInformation.fatherName"
            socialStatusId="contact.contactIdentityInformation.socialStatusId"
            documentNo="contact.contactIdentityInformation.documentNo"
            validDate="contact.contactIdentityInformation.validDate"
            genderId="contact.contactIdentityInformation.genderId"
          />
          <ForeignInput
            identityCardTypeId={
              formik.values.contact.contactIdentityInformation
                .identityCardTypeId
            }
            documentNo="contact.contactIdentityInformation.documentNo"
            validDate="contact.contactIdentityInformation.validDate"
            genderId="contact.contactIdentityInformation.genderId"
          />
          <BluecardInput
            identityCardTypeId={
              formik.values.contact.contactIdentityInformation
                .identityCardTypeId
            }
            fatherName="contact.contactIdentityInformation.fatherName"
            motherName="contact.contactIdentityInformation.motherName"
            placeOfBirth="contact.contactIdentityInformation.placeOfBirth"
            serialNo="contact.contactIdentityInformation.serialNo"
            documentNo="contact.contactIdentityInformation.documentNo"
            genderId="contact.contactIdentityInformation.genderId"
          />
          <TCForeignInput
            identityCardTypeId={
              formik.values.contact.contactIdentityInformation
                .identityCardTypeId
            }
            socialStatusId="contact.contactIdentityInformation.socialStatusId"
            documentNo="contact.contactIdentityInformation.documentNo"
            genderId="contact.contactIdentityInformation.genderId"
            validDate="contact.contactIdentityInformation.validDate"
          />
        </>
      )}

      <Grid item md={12}>
        <Grid container spacing={4}>
          <ContactInfoArray
            contactCommunication="contact.contactCommunication"
            contactCommunicationValue={
              formik.values.contact.contactCommunication
            }
          />
        </Grid>
      </Grid>
    </Grid>
  );
};

export default RelatedPersonCreate;
